import React, { useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { availableLanguages } from '../../locales';
import useCooldown from '../vedic/shared/hooks/useCooldown';
import deriveGaneshaReading from '../vedic/prashnavali/ganesha/utils/deriveGaneshaReading';
import GaneshaLanding from '../vedic/prashnavali/ganesha/GaneshaLanding';
import GaneshaGrid from '../vedic/prashnavali/ganesha/GaneshaGrid';
import GaneshaResultCard from '../vedic/prashnavali/ganesha/GaneshaResultCard';
import GaneshaShareImage from '../vedic/prashnavali/ganesha/GaneshaShareImage';
import ganeshaData from '../vedic/prashnavali/ganesha/data/ganesha_prashnavali.json';

const COOLDOWN_STORAGE_KEY = 'prashnavali_ganesha_last_query';
const COOLDOWN_DURATION_MS = 60 * 60 * 1000;

const PageContainer = styled.main`
  min-height: 100vh;
  background: #FFF9F0;
  padding: 24px 20px 60px;

  @media (max-width: ${p => p.theme.breakpoints.tablet}px) {
    padding: 16px 16px 40px;
  }
`;

const TopBar = styled.div`
  max-width: 720px;
  margin: 0 auto ${p => p.theme.spacing[4]}px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const BackLink = styled.button`
  background: none;
  border: none;
  color: #8B3A00;
  font-size: 14px;
  cursor: pointer;
  padding: 0;
  &:hover { text-decoration: underline; }
`;

const LangToggle = styled.div`
  display: flex;
  gap: 4px;
`;

const LangBtn = styled.button`
  padding: 6px 14px;
  border: 1px solid ${p => p.active ? '#EF6C00' : 'rgba(239,108,0,0.25)'};
  background: ${p => p.active ? '#EF6C00' : 'transparent'};
  color: ${p => p.active ? '#fff' : '#8B3A00'};
  border-radius: 4px;
  font-size: 13px;
  font-weight: ${p => p.active ? 600 : 400};
  cursor: pointer;
  transition: all 0.2s;
  font-family: 'Noto Sans Tamil', sans-serif;
`;

const GaneshaPrashnavali = () => {
  const navigate = useNavigate();
  const [view, setView] = useState('landing');
  const [lang, setLang] = useState('ta');
  const [selectedCellIndex, setSelectedCellIndex] = useState(null);
  const [sharePreviewOpen, setSharePreviewOpen] = useState(false);

  const cooldown = useCooldown({
    storageKey: COOLDOWN_STORAGE_KEY,
    durationMs: COOLDOWN_DURATION_MS,
  });

  const reading = useMemo(() => {
    if (selectedCellIndex === null) return null;
    return deriveGaneshaReading(selectedCellIndex, ganeshaData);
  }, [selectedCellIndex]);

  const handleBegin = useCallback(() => {
    if (cooldown.active) return;
    setView('grid');
  }, [cooldown.active]);

  const handleCellSelect = useCallback((cellIndex) => {
    setSelectedCellIndex(cellIndex);
    setView('result');
  }, []);

  const handleReturn = useCallback(() => {
    if (selectedCellIndex !== null) {
      cooldown.startCooldown(selectedCellIndex);
    }
    setSelectedCellIndex(null);
    setView('landing');
  }, [selectedCellIndex, cooldown]);

  const handleViewLastReading = useCallback(() => {
    if (cooldown.lastReadingCellIndex !== null) {
      setSelectedCellIndex(cooldown.lastReadingCellIndex);
      setView('result');
    }
  }, [cooldown.lastReadingCellIndex]);

  return (
    <PageContainer>
      <TopBar>
        <BackLink onClick={() => navigate('/vedic-tools')}>
          &larr; {lang === 'ta' ? 'கருவிகள்' : 'Tools'}
        </BackLink>
        <LangToggle>
          {availableLanguages.map(l => (
            <LangBtn key={l.code} active={lang === l.code} onClick={() => setLang(l.code)}>
              {l.label}
            </LangBtn>
          ))}
        </LangToggle>
      </TopBar>

      {view === 'landing' && (
        <GaneshaLanding
          onBegin={handleBegin}
          onViewLastReading={handleViewLastReading}
          cooldown={cooldown}
          lang={lang}
        />
      )}

      {view === 'grid' && (
        <GaneshaGrid
          data={ganeshaData}
          onCellSelect={handleCellSelect}
          lang={lang}
        />
      )}

      {view === 'result' && reading && (
        <GaneshaResultCard
          cellIndex={reading.cell}
          reading={reading}
          onShare={() => setSharePreviewOpen(true)}
          onReturn={handleReturn}
          lang={lang}
        />
      )}

      {sharePreviewOpen && reading && (
        <GaneshaShareImage
          open={sharePreviewOpen}
          onClose={() => setSharePreviewOpen(false)}
          cellIndex={reading.cell}
          reading={reading}
          lang={lang}
        />
      )}
    </PageContainer>
  );
};

export default GaneshaPrashnavali;
